import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import OptimizedImage, { getImageUrl, getThumbnailUrl } from './OptimizedImage';
import './ProductDescription.css';

export const EnhancedProductCard = ({ product, featured = false }) => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    navigate(`/product/${product.id}`);
  };

  const hasOptions = Object.keys(product.base_options || {}).length > 0;
  const isCustomizable = Object.keys(product.custom_options || {}).length > 0;
  const hasAddons = (product.addons || []).length > 0;

  // Get optimized image URLs
  const mainImage = product.images && product.images.length > 0 ? product.images[0] : null;
  const imageUrl = mainImage ? getImageUrl(mainImage) : '';
  const thumbnailUrl = mainImage ? getThumbnailUrl(mainImage) : null;

  return (
    <div
      className={`group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ${
        featured ? 'shadow-2xl' : 'shadow-lg'
      } ${
        isHovered ? 'scale-105 shadow-2xl' : 'scale-100'
      } ${
        isDark 
          ? 'bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700' 
          : 'bg-white border border-gray-100'
      }`}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Featured Badge */}
      {featured && (
        <div className="absolute top-4 left-4 z-10">
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            Featured
          </div>
        </div>
      )}

      {/* Image Container */}
      <div className={`relative ${featured ? 'aspect-[4/3]' : 'aspect-square'} overflow-hidden`}>
        {imageUrl ? (
          <>
            <OptimizedImage
              src={imageUrl}
              thumbnailSrc={thumbnailUrl}
              alt={product.name}
              className={`transition-transform duration-700 ${
                isHovered ? 'scale-110' : 'scale-100'
              }`}
              aspectRatio={featured ? 'landscape' : 'square'}
              priority={featured} // Featured images load with priority
            />
            {/* Gradient Overlay on Hover */}
            <div className={`absolute inset-0 bg-gradient-to-t ${
              isDark 
                ? 'from-gray-900/80 via-gray-900/20 to-transparent' 
                : 'from-black/60 via-black/20 to-transparent'
            } opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
            
            {/* Quick View Button */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0">
              <button className="bg-white text-gray-900 px-6 py-3 rounded-full font-semibold shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                View Details
              </button>
            </div>
          </>
        ) : (
          <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${
            isDark ? 'from-gray-700 to-gray-800' : 'from-gray-50 to-gray-100'
          }`}>
            <div className="text-center">
              <svg className={`w-16 h-16 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No image</span>
            </div>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-5 space-y-3">
        {/* Category Badge */}
        {product.category && (
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium px-3 py-1 rounded-full ${
              isDark 
                ? 'bg-blue-900/50 text-blue-300' 
                : 'bg-blue-50 text-blue-600'
            }`}>
              {product.category}
            </span>
          </div>
        )}

        {/* Product Name */}
        <h3 className={`font-bold ${featured ? 'text-xl' : 'text-lg'} line-clamp-2 ${
          isDark ? 'text-white' : 'text-gray-900'
        } group-hover:text-blue-600 transition-colors duration-300`}>
          {product.name}
        </h3>

        {/* Product Description */}
        <div 
          className={`product-description-content product-description-preview text-sm ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}
          dangerouslySetInnerHTML={{ __html: product.description }}
        />

        {/* Features */}
        <div className="flex flex-wrap gap-1.5">
          {hasOptions && (
            <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md ${
              isDark 
                ? 'bg-purple-900/30 text-purple-300' 
                : 'bg-purple-50 text-purple-600'
            }`}>
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
              Options
            </div>
          )}
          {isCustomizable && (
            <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md ${
              isDark 
                ? 'bg-green-900/30 text-green-300' 
                : 'bg-green-50 text-green-600'
            }`}>
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              Custom
            </div>
          )}
          {hasAddons && (
            <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md ${
              isDark 
                ? 'bg-orange-900/30 text-orange-300' 
                : 'bg-orange-50 text-orange-600'
            }`}>
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" />
              </svg>
              Add-ons
            </div>
          )}
        </div>

        {/* Price and CTA */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Starting at</div>
            <div className={`text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent`}>
              ${product.price?.toFixed(2) || '0.00'}
            </div>
          </div>
          <button className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 ${
            isDark 
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-500 hover:to-purple-500' 
              : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700'
          } shadow-lg hover:shadow-xl`}>
            View
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnhancedProductCard;
